// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Badge,
  Loader,
  Center,
  Alert,
  Grid,
  Rating,
  Stepper,
  Timeline,
  Anchor,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DateTimePicker } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconUser,
  IconCalendar,
  IconCheck,
  IconX,
  IconStar,
  IconSend,
  IconMail,
  IconPhone,
  IconFileText,
  IconMessage,
} from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import {
  useApplicant,
  useUpdateApplicantStatus,
  useAddApplicantNote,
  useRateApplicant,
  useScheduleInterview,
  useInterviews,
} from '../../hooks/useRecruitment';
import type {
  ApplicantStatus,
  ApplicantNote,
  Interview,
  InterviewType,
  ScheduleInterviewInput,
} from '../../types/recruitment';

const STAGE_ORDER: ApplicantStatus[] = [
  'NEW',
  'SCREENING',
  'PHONE_SCREEN',
  'INTERVIEW',
  'ASSESSMENT',
  'OFFER',
  'HIRED',
];

const STAGE_LABELS: Record<string, string> = {
  NEW: 'New',
  SCREENING: 'Screening',
  PHONE_SCREEN: 'Phone Screen',
  INTERVIEW: 'Interview',
  ASSESSMENT: 'Assessment',
  OFFER: 'Offer',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn',
};

const INTERVIEW_TYPE_OPTIONS: { value: InterviewType; label: string }[] = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'IN_PERSON', label: 'In Person' },
  { value: 'PANEL', label: 'Panel' },
  { value: 'TECHNICAL', label: 'Technical' },
];

export default function ApplicantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [noteContent, setNoteContent] = useState('');
  const [interviewModalOpen, setInterviewModalOpen] = useState(false);
  const [interviewForm, setInterviewForm] = useState({
    interviewerId: '',
    scheduledAt: null as string | null,
    duration: 60,
    type: 'VIDEO' as InterviewType,
    location: '',
  });

  const { data: rawApplicant, isLoading, error } = useApplicant(id!);
  const applicant = rawApplicant as any;
  const { data: rawInterviews } = useInterviews({ applicantId: id });
  const interviews = rawInterviews as any;
  const updateStatusMutation = useUpdateApplicantStatus();
  const addNoteMutation = useAddApplicantNote();
  const rateMutation = useRateApplicant();
  const scheduleInterviewMutation = useScheduleInterview();

  const currentStageIndex = applicant
    ? STAGE_ORDER.indexOf(applicant.status as ApplicantStatus)
    : -1;

  const handleNextStage = async () => {
    if (!applicant || currentStageIndex < 0 || currentStageIndex >= STAGE_ORDER.length - 1) return;
    const nextStatus = STAGE_ORDER[currentStageIndex + 1] as ApplicantStatus;
    try {
      await updateStatusMutation.mutateAsync({ id: applicant.id, status: nextStatus });
      notifications.show({
        title: 'Updated',
        message: `Moved to ${STAGE_LABELS[nextStatus]}`,
        color: 'green',
      });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to update status', color: 'red' });
    }
  };

  const handleReject = async () => {
    if (!applicant) return;
    try {
      await updateStatusMutation.mutateAsync({ id: applicant.id, status: 'REJECTED' });
      notifications.show({ title: 'Rejected', message: 'Applicant has been rejected', color: 'red' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to reject applicant', color: 'red' });
    }
  };

  const handleMakeOffer = async () => {
    if (!applicant) return;
    try {
      await updateStatusMutation.mutateAsync({ id: applicant.id, status: 'OFFER' });
      notifications.show({ title: 'Offer Sent', message: 'Offer stage activated', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to make offer', color: 'red' });
    }
  };

  const handleAddNote = async () => {
    if (!applicant || !noteContent.trim()) return;
    try {
      await addNoteMutation.mutateAsync({ id: applicant.id, content: noteContent.trim() });
      setNoteContent('');
      notifications.show({ title: 'Note Added', message: 'Note saved', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to add note', color: 'red' });
    }
  };

  const handleRate = async (rating: number) => {
    if (!applicant) return;
    try {
      await rateMutation.mutateAsync({ id: applicant.id, rating });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to update rating', color: 'red' });
    }
  };

  const handleScheduleInterview = async () => {
    if (!applicant || !interviewForm.scheduledAt || !interviewForm.interviewerId) return;
    const input: ScheduleInterviewInput = {
      applicantId: applicant.id,
      interviewerId: interviewForm.interviewerId,
      scheduledAt: interviewForm.scheduledAt ?? '',
      duration: interviewForm.duration,
      type: interviewForm.type,
      location: interviewForm.location || undefined,
    };

    try {
      await scheduleInterviewMutation.mutateAsync(input);
      notifications.show({ title: 'Scheduled', message: 'Interview scheduled successfully', color: 'green' });
      setInterviewModalOpen(false);
      setInterviewForm({
        interviewerId: '',
        scheduledAt: null,
        duration: 60,
        type: 'VIDEO',
        location: '',
      });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to schedule interview', color: 'red' });
    }
  };

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !applicant) {
    return (
      <Alert color="red" title="Error">
        Failed to load applicant details.
      </Alert>
    );
  }

  const isTerminalStatus = applicant.status === 'REJECTED' || applicant.status === 'WITHDRAWN' || applicant.status === 'HIRED';

  return (
    <Stack gap="md">
      <PageHeader
        title={`${applicant.firstName} ${applicant.lastName}`}
        breadcrumbs={[
          { label: 'Recruitment', to: '/admin/workforce/recruiting' },
          { label: applicant.jobTitle, to: `/recruitment/postings/${applicant.jobPostingId}` },
          { label: `${applicant.firstName} ${applicant.lastName}` },
        ]}
        actions={
          <Group gap="sm">
            {!isTerminalStatus && (
              <>
                <Button
                  variant="light"
                  icon={<IconCalendar size={16} />}
                  onClick={() => setInterviewModalOpen(true)}
                >
                  Schedule Interview
                </Button>
                {currentStageIndex >= 0 && currentStageIndex < STAGE_ORDER.length - 1 && (
                  <Button
                    icon={<IconCheck size={16} />}
                    onClick={handleNextStage}
                    loading={updateStatusMutation.isPending}
                  >
                    Move to {STAGE_LABELS[STAGE_ORDER[currentStageIndex + 1] ?? '']}
                  </Button>
                )}
                <Button
                  variant="light"
                  color="orange"
                  icon={<IconSend size={16} />}
                  onClick={handleMakeOffer}
                >
                  Make Offer
                </Button>
                <Button
                  variant="light"
                  color="red"
                  icon={<IconX size={16} />}
                  onClick={handleReject}
                >
                  Reject
                </Button>
              </>
            )}
          </Group>
        }
      />

      {/* Header Card */}
      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" ta="flex-start">
          <Stack gap="xs">
            <Group gap="sm">
              <Text size="xl" fw={700}>
                {applicant.firstName} {applicant.lastName}
              </Text>
              <StatusBadge status={applicant.status} />
              <Badge
                variant="light"
                color={
                  applicant.source === 'REFERRAL'
                    ? 'green'
                    : applicant.source === 'LINKEDIN'
                      ? 'cyan'
                      : 'gray'
                }
              >
                {applicant.source.replace(/_/g, ' ')}
              </Badge>
            </Group>
            <Group gap="lg">
              <Group gap={6}>
                <IconMail size={14} color="var(--mantine-color-dimmed)" />
                <Text size="sm">{applicant.email}</Text>
              </Group>
              <Group gap={6}>
                <IconPhone size={14} color="var(--mantine-color-dimmed)" />
                <Text size="sm">{applicant.phone}</Text>
              </Group>
            </Group>
            <Group gap="md">
              {applicant.resumeUrl && (
                <Anchor href={applicant.resumeUrl} target="_blank" size="sm">
                  <Group gap={4}>
                    <IconFileText size={14} />
                    Resume
                  </Group>
                </Anchor>
              )}
              {applicant.coverLetterUrl && (
                <Anchor href={applicant.coverLetterUrl} target="_blank" size="sm">
                  <Group gap={4}>
                    <IconFileText size={14} />
                    Cover Letter
                  </Group>
                </Anchor>
              )}
            </Group>
          </Stack>
          <Stack ta="center" gap={4}>
            <Text size="xs" c="dimmed" fw={500}>
              Rating
            </Text>
            <Rating value={applicant.rating} onChange={handleRate} size="md" />
          </Stack>
        </Group>
      </Paper>

      {/* Stage Progression */}
      {!isTerminalStatus && (
        <Paper withBorder p="lg" radius="md">
          <Text fw={600} mb="md">
            Hiring Pipeline
          </Text>
          <Stepper
            active={currentStageIndex >= 0 ? currentStageIndex : 0}
            size="sm"
          >
            {STAGE_ORDER.map((stage) => (
              <Stepper.Step
                key={stage}
                label={STAGE_LABELS[stage]}
              />
            ))}
          </Stepper>
        </Paper>
      )}

      <Grid>
        {/* Left Column: Timeline & Notes */}
        <Grid.Col span={{ base: 12, md: 7 }}>
          {/* Notes Section */}
          <Paper withBorder p="lg" radius="md" mb="md">
            <Text fw={600} mb="md">
              Notes
            </Text>
            <Stack gap="sm" mb="md">
              <Textarea
                placeholder="Add a note..."
                value={noteContent}
                onChange={(e) => setNoteContent(e.currentTarget.value)}
                minRows={2}
              />
              <Group justify="flex-end">
                <Button
                  size="sm"
                  onClick={handleAddNote}
                  loading={addNoteMutation.isPending}
                  disabled={!noteContent.trim()}
                >
                  Add Note
                </Button>
              </Group>
            </Stack>

            {applicant.notes.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="md">
                No notes yet
              </Text>
            ) : (
              <Timeline active={applicant.notes.length - 1} bulletSize={24} lineWidth={2}>
                {applicant.notes.map((note: ApplicantNote) => (
                  <Timeline.Item
                    key={note.id}
                    bullet={<IconMessage size={12} />}
                    title={
                      <Group gap="xs">
                        <Text size="sm" fw={600}>
                          {note.authorName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {new Date(note.createdAt).toLocaleString()}
                        </Text>
                      </Group>
                    }
                  >
                    <Text size="sm" mt={4}>
                      {note.content}
                    </Text>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </Paper>
        </Grid.Col>

        {/* Right Column: Interviews */}
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Paper withBorder p="lg" radius="md">
            <Group justify="space-between" mb="md">
              <Text fw={600}>Interviews</Text>
              {!isTerminalStatus && (
                <Button
                  size="xs"
                  variant="light"
                  icon={<IconCalendar size={14} />}
                  onClick={() => setInterviewModalOpen(true)}
                >
                  Schedule
                </Button>
              )}
            </Group>

            {!interviews || interviews.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="md">
                No interviews scheduled
              </Text>
            ) : (
              <Stack gap="sm">
                {interviews.map((interview: Interview) => (
                  <Paper key={interview.id} withBorder p="sm" radius="sm">
                    <Group justify="space-between" mb={4}>
                      <Badge
                        variant="light"
                        color={
                          interview.type === 'TECHNICAL'
                            ? 'violet'
                            : interview.type === 'PANEL'
                              ? 'indigo'
                              : 'blue'
                        }
                        size="xs"
                      >
                        {interview.type}
                      </Badge>
                      <StatusBadge status={interview.status} size="xs" />
                    </Group>
                    <Text size="sm" fw={500}>
                      {interview.interviewerName}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {new Date(interview.scheduledAt).toLocaleString()} ({interview.duration} min)
                    </Text>
                    {interview.location && (
                      <Text size="xs" c="dimmed">
                        {interview.location}
                      </Text>
                    )}
                    {interview.feedback && (
                      <Paper bg="gray.0" p="xs" mt="xs" radius="sm">
                        <Group gap={4} mb={2}>
                          <IconStar size={12} />
                          <Text size="xs" fw={500}>
                            Rating: {interview.rating}/5
                          </Text>
                        </Group>
                        <Text size="xs">{interview.feedback}</Text>
                      </Paper>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Schedule Interview Modal */}
      <Modal
        opened={interviewModalOpen}
        onClose={() => setInterviewModalOpen(false)}
        title="Schedule Interview"
        size="md"
      >
        <Stack gap="sm">
          <TextInput
            label="Interviewer ID"
            required
            value={interviewForm.interviewerId}
            onChange={(e) =>
              setInterviewForm((f) => ({ ...f, interviewerId: e.currentTarget.value }))
            }
            placeholder="Interviewer employee ID"
          />

          <DateTimePicker
            label="Date & Time"
            required
            value={interviewForm.scheduledAt}
            onChange={(v) => setInterviewForm((f) => ({ ...f, scheduledAt: v }))}
            placeholder="Select date and time"
          />

          <Group grow>
            <NumberInput
              label="Duration (minutes)"
              value={interviewForm.duration}
              onChange={(v) =>
                setInterviewForm((f) => ({ ...f, duration: typeof v === 'number' ? v : 60 }))
              }
              min={15}
              max={480}
              step={15}
            />
            <Select
              label="Type"
              required
              value={interviewForm.type}
              onChange={(v) =>
                setInterviewForm((f) => ({
                  ...f,
                  type: (v as InterviewType) || 'VIDEO',
                }))
              }
              data={INTERVIEW_TYPE_OPTIONS}
            />
          </Group>

          <TextInput
            label="Location"
            value={interviewForm.location}
            onChange={(e) =>
              setInterviewForm((f) => ({ ...f, location: e.currentTarget.value }))
            }
            placeholder="Room / Zoom link / Address"
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setInterviewModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleScheduleInterview}
              loading={scheduleInterviewMutation.isPending}
              disabled={!interviewForm.interviewerId || !interviewForm.scheduledAt}
            >
              Schedule
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
